/** @jsxImportSource @emotion/react */
import React from 'react'
import colors from '@sportninja/common/constants/appColors'
import Text from '../../../ts-components/Text'
import { Team } from '@sportninja/common/types/TeamStanding'
import { OrganizationTreeNode } from 'src/context/MyScheduleContext/types'
import TargetAudienceSelector from '../TargetAudienceSelector'
import EntityDisplay from '../EntityDisplay'
import FeedPostModalIconButtons from '../FeedPostModalIconButtons'
import { ErrorLabel } from 'src/components/ts-components/ErrorLabel'
import {
  headerContainerStyle,
  headerContentStyle,
  avatarStyle,
  userInfoColumnStyle,
  userNameRowStyle,
  postToRowStyle,
} from './styles'

interface PostHeaderProps {
  selfUser: any
  entity?: any
  entityType: string
  orgTree: OrganizationTreeNode[]
  teams: Team[]
  selected?: string
  setSelected: (value: any) => void
  selectedType: string | null
  setSelectedType: (value: string | null) => void
  targetAudience: number | null
  setTargetAudience: (value: number | null) => void
  setParentId: (value: string) => void
  setParentEntityType: (
    value: 'team' | 'schedule' | 'organization' | undefined
  ) => void
  isLoading: boolean
  postToEdit: any
  isLoadingEntities: boolean
  showImage: boolean
  showLink: boolean
  showSendVia: boolean
  onToggleSection: (section: 'image' | 'link' | 'sendVia') => void
  fieldErrors: string[]
  customError: any
  name: string
  hasMoreTeams?: boolean
  isLoadingMoreTeams?: boolean
  onLoadMoreTeams?: () => void
  onTeamsSearch?: (query: string) => void
}

/**
 * PostHeader component - displays user avatar, name, and target audience selector
 */
export const PostHeader: React.FC<PostHeaderProps> = ({
  selfUser,
  entity,
  entityType,
  orgTree,
  teams,
  selected,
  setSelected,
  selectedType,
  setSelectedType,
  targetAudience,
  setTargetAudience,
  setParentId,
  setParentEntityType,
  isLoading,
  postToEdit,
  isLoadingEntities,
  showImage,
  showLink,
  showSendVia,
  onToggleSection,
  fieldErrors,
  customError,
  name,
  hasMoreTeams,
  isLoadingMoreTeams,
  onLoadMoreTeams,
  onTeamsSearch,
}) => {
  'use memo'

  return (
    <div css={headerContainerStyle}>
      <div css={headerContentStyle}>
        <img
          css={avatarStyle}
          src={
            selfUser?.imageId ||
            '/images/generic_placeholders/placeholder_player.png'
          }
          alt='User Avatar'
        />
        <div css={userInfoColumnStyle}>
          <div css={userNameRowStyle}>
            <Text
              variant='paragraphMedium'
              weight='bold'
              color={colors.NEUTRAL_0}
            >
              {selfUser?.fullName}
            </Text>
          </div>
          <div css={postToRowStyle}>
            {entity ? (
              <EntityDisplay entity={entity} entityType={entityType} />
            ) : (
              <TargetAudienceSelector
                orgTree={orgTree}
                teams={teams}
                selected={selected}
                setSelected={setSelected}
                selectedType={selectedType}
                setSelectedType={setSelectedType}
                targetAudience={targetAudience}
                setTargetAudience={setTargetAudience}
                setParentId={setParentId}
                setParentEntityType={setParentEntityType}
                disabled={isLoading || postToEdit !== null || isLoadingEntities}
                isLoadingEntities={isLoadingEntities}
                hasMoreTeams={hasMoreTeams}
                isLoadingMoreTeams={isLoadingMoreTeams}
                onLoadMoreTeams={onLoadMoreTeams}
                onTeamsSearch={onTeamsSearch}
              />
            )}
          </div>
          <ErrorLabel
            fieldErrors={fieldErrors}
            customError={customError}
            name={name}
          />
        </div>
      </div>
      <FeedPostModalIconButtons
        showImage={showImage}
        showLink={showLink}
        onToggle={onToggleSection}
        sendViaSelected={showSendVia}
        onSendViaClick={() => onToggleSection('sendVia')}
      />
    </div>
  )
}
